import Map from 'ol/Map';
import { createContext, useContext, useMemo } from 'react';
import { shapeToVectorLayer as originalShapeToVectorLayer } from './tranformations/shapeToVectorLayer';
import { getCircleTrueRadius as originalGetCircleTrueRadius } from './calculateCircleRadius';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Circle } from 'ol/geom';

export type OpenLayersContextInterface = Readonly<{
  map: Map;
  displayProjection: string;
  dataProjection: string;
  // Wrapper functions that automatically inject projections
  shapeToVectorLayer: (
    shape: GeoJson,
  ) => ReturnType<typeof originalShapeToVectorLayer>;
  getCircleTrueRadius: (circle: Circle) => number;
}>;

const OpenLayersContext = createContext<OpenLayersContextInterface | undefined>(
  undefined,
);

export const OpenLayersProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: { map: Map; displayProjection: string; dataProjection: string };
}) => {
  // Create wrapper functions that automatically inject projections
  const contextValue = useMemo(
    (): OpenLayersContextInterface => ({
      map: value.map,
      displayProjection: value.displayProjection,
      dataProjection: value.dataProjection,
      shapeToVectorLayer: (shape: GeoJson) =>
        originalShapeToVectorLayer(
          shape,
          value.displayProjection,
          value.dataProjection,
        ),
      getCircleTrueRadius: (circle: Circle) =>
        originalGetCircleTrueRadius(
          circle,
          value.displayProjection,
          value.dataProjection,
        ),
    }),
    [value.map, value.displayProjection, value.dataProjection],
  );

  return (
    <OpenLayersContext.Provider value={contextValue}>
      {children}
    </OpenLayersContext.Provider>
  );
};

export function useOpenLayersContext(): OpenLayersContextInterface {
  const context = useContext(OpenLayersContext);
  if (context == null) {
    throw new Error(
      'No context provided: useOpenLayersContext() can only be used in a descendant of <MapContainer>',
    );
  }
  return context;
}
